/**
 * RENDA-K — 読み込んだオーディオの指定位置を BPM 同期で連打する
 * スタッターエフェクトツール（実験的機能）。Stutter Edit 系の簡易版。
 *
 * 間隔カーブ（固定 / 直線 / 曲線 + 量子化）・スライス加速（SPEED）・
 * アッチェレランド（ACCEL）を備え、元音源の指定区間を連打に差し替えた
 * 完成形を WAV 書き出しする。
 *
 * UI はスプラトゥーン風のかわいいマシンガン（クリーム地 + 濃紺縁取り +
 * インクスプラット）をモチーフに、再生ボタンは遊技機の PUSH ボタン風。
 * ノブキャップには SOREMO ブランドカラーを使う。
 */

import React, { useCallback, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import IconButton from "../../../components/button/IconButton";
import ToolTip from "../../../components/tooltip/ToolTip";
import Knob from "./components/Knob";
import PanelSection from "./components/PanelSection";
import PresetBar from "./components/PresetBar";
import PushButton from "./components/PushButton";
import ToggleSwitch from "./components/ToggleSwitch";
import IntervalEditor from "./components/IntervalEditor";
import WaveformDisplay from "./components/WaveformDisplay";
import { useStutterEngine } from "./hooks/useStutterEngine";
import {
  decodeAudioFile,
  formatSec,
  type LoadedAudio,
} from "./utils/audioLoad";
import {
  RENDA_K_SCHEMA_VERSION,
  RENDA_K_TOOL_ID,
  buildPresetPayload,
  sanitizePresetPayload,
  type RendaKPresetPayload,
} from "./utils/presetPayload";
import { renderStutterToWav } from "./utils/renderStutter";
import {
  DEFAULT_RENDA_PARAMS,
  INTERVAL_STEPS,
  buildStutterPlan,
  phraseLengthSec,
  speedToPlaybackRate,
  type RendaParams,
} from "./utils/stutterPlan";
import { downloadBlob } from "../phrase-bloom/utils/midiWriter";
import { RENDA_COLORS, RENDA_FONT, RENDA_PANEL } from "./palette";

/** ？アイコンのホバーで表示するツールの説明 */
const HELP_DESCRIPTION = [
  "読み込んだオーディオの指定位置を、BPM に同期して連打（スタッター）させるツール（実験的機能）。",
  "波形をクリック / ドラッグして連打開始位置を決めます（Shift で微調整）。",
  "間隔エディタの左右ハンドルで連打間隔（64分〜2分音符）の開始・終了を決め、CURVE で変化カーブを曲げます（同じ高さ = 固定、違う高さ = 直線、CURVE ≠ 0 = 曲線）。",
  "QUANTIZE を ON にすると各連打の間隔が音価にスナップします（OFF はなめらかに変化）。",
  "SPEED は各スライスの再生速度（0 = 等倍でちょうど音符の長さ。上げるとピッチも上がる）、ACCEL は時間経過で間隔を詰めるアッチェレランドです。",
  "PUSH で連打開始位置の 1 小節前から試聴できます。押すたびにリトリガ（撃ち直し）します。",
  "WAV は元音源の該当区間を連打に差し替えた完成形（全体の長さは元のまま）を書き出します。",
  "ノブは上下ドラッグまたはホイールで操作、Shift で微調整、ダブルクリックで初期値に戻ります。",
].join("\n");

// ---- フェイスプレート（トイガンのボディ） ----

const Faceplate = styled.div`
  width: 100%;
  border-radius: 24px;
  border: 3px solid ${RENDA_PANEL.outline};
  background: linear-gradient(
    165deg,
    ${RENDA_PANEL.face} 0%,
    ${RENDA_PANEL.faceEdge} 100%
  );
  box-shadow:
    0 18px 40px rgba(37, 42, 64, 0.25),
    inset 0 2px 0 rgba(255, 255, 255, 0.9);
  padding: 30px 24px 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

/** 四隅のインクスプラット（コーナーごとに色と傾きを変える） */
const SPLAT_CORNERS = {
  tl: {
    place: "top: 8px; left: 10px;",
    rotate: -12,
    color: RENDA_COLORS.inkPink,
  },
  tr: {
    place: "top: 8px; right: 10px;",
    rotate: 24,
    color: RENDA_COLORS.inkLime,
  },
  bl: {
    place: "bottom: 8px; left: 10px;",
    rotate: 96,
    color: RENDA_COLORS.inkCyan,
  },
  br: {
    place: "bottom: 8px; right: 10px;",
    rotate: 210,
    color: RENDA_COLORS.inkOrange,
  },
} as const;

const SplatCorner = styled.span<{ $corner: keyof typeof SPLAT_CORNERS }>`
  position: absolute;
  ${({ $corner }) => SPLAT_CORNERS[$corner].place}
  transform: rotate(${({ $corner }) => SPLAT_CORNERS[$corner].rotate}deg);
  opacity: 0.85;
  pointer-events: none;
  line-height: 0;
`;

const InkSplat: React.FC<{ color: string }> = ({ color }) => (
  <svg viewBox="0 0 40 40" width={30} height={30} aria-hidden="true">
    <circle cx="20" cy="20" r="10" fill={color} />
    <circle cx="32" cy="13" r="4" fill={color} />
    <circle cx="9" cy="30" r="3" fill={color} />
    <circle cx="31" cy="30" r="2.5" fill={color} />
    <circle cx="8" cy="11" r="2" fill={color} />
    <circle cx="22" cy="35" r="1.8" fill={color} />
  </svg>
);

/** ブランド行の飾り: かわいいスプラ風マシンガン */
const SplatGunIcon: React.FC = () => (
  <svg viewBox="0 0 64 40" width={45} height={28} aria-hidden="true">
    {/* インクタンク */}
    <circle
      cx="13"
      cy="20"
      r="9"
      fill={RENDA_COLORS.inkPink}
      stroke={RENDA_PANEL.outline}
      strokeWidth="3"
    />
    <ellipse cx="10" cy="16.5" rx="3" ry="2" fill="rgba(255,255,255,0.5)" />
    {/* グリップ */}
    <rect
      x="24"
      y="24"
      width="8"
      height="12"
      rx="3"
      fill={RENDA_COLORS.inkOrange}
      stroke={RENDA_PANEL.outline}
      strokeWidth="3"
      transform="rotate(14 28 30)"
    />
    {/* ボディ */}
    <rect
      x="17"
      y="11"
      width="27"
      height="17"
      rx="8"
      fill={RENDA_COLORS.soremoBlue}
      stroke={RENDA_PANEL.outline}
      strokeWidth="3"
    />
    <ellipse cx="26" cy="16" rx="5" ry="2.4" fill="rgba(255,255,255,0.4)" />
    {/* バレル */}
    <rect
      x="42"
      y="14.5"
      width="15"
      height="11"
      rx="5.5"
      fill="#fffbef"
      stroke={RENDA_PANEL.outline}
      strokeWidth="3"
    />
    {/* マズル */}
    <circle
      cx="57"
      cy="20"
      r="4.5"
      fill={RENDA_COLORS.inkYellow}
      stroke={RENDA_PANEL.outline}
      strokeWidth="3"
    />
  </svg>
);

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 16px;
`;

const BrandTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

const BrandName = styled.h1`
  margin: 0;
  font-family: ${RENDA_FONT};
  font-weight: 800;
  font-size: 22px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: ${RENDA_PANEL.engravingStrong};
`;

const BrandSub = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: ${RENDA_FONT};
  font-weight: 800;
  font-size: 10px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${RENDA_PANEL.engraving};
`;

/** ノブ系セクション（PC では 2 列） */
const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px 14px;
  align-items: stretch;

  @media (min-width: 720px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

// ---- トランスポート・ボタン類 ----

const TransportRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 14px 22px;
  border-top: 2px dashed ${RENDA_PANEL.sectionBorder};
  padding-top: 20px;
`;

const InkButton = styled.button<{ $accent?: string }>`
  font-family: ${RENDA_FONT};
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #fffbef;
  background: ${({ $accent }) => $accent ?? RENDA_PANEL.outline};
  border: 3px solid ${RENDA_PANEL.outline};
  border-radius: 999px;
  padding: 8px 20px;
  cursor: pointer;
  box-shadow: 0 3px 0 rgba(37, 42, 64, 0.55);
  transition:
    transform 0.06s ease-out,
    box-shadow 0.06s ease-out;

  &:hover:not(:disabled) {
    filter: brightness(1.12);
  }

  &:active:not(:disabled) {
    transform: translateY(2px);
    box-shadow: 0 1px 0 rgba(37, 42, 64, 0.55);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${RENDA_PANEL.focus};
    outline-offset: 2px;
  }
`;

const InfoPill = styled.span`
  font-family: ${RENDA_FONT};
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.06em;
  color: ${RENDA_PANEL.displayText};
  background: ${RENDA_PANEL.displayBg};
  border-radius: 999px;
  padding: 6px 14px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

// ---- ページ本体 ----

const RendaK: React.FC = () => {
  const [params, setParams] = useState<RendaParams>(DEFAULT_RENDA_PARAMS);
  const [audio, setAudio] = useState<LoadedAudio | null>(null);
  const [isDecoding, setIsDecoding] = useState(false);
  const [isRenderingWav, setIsRenderingWav] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const hits = useMemo(() => buildStutterPlan(params), [params]);
  const phraseLenSec = phraseLengthSec(params.lengthSixteenths, params.bpm);

  const engine = useStutterEngine({
    buffer: audio?.buffer ?? null,
    params,
  });
  const engineStop = engine.stop;

  const setParam = useCallback(
    <K extends keyof RendaParams>(key: K, value: RendaParams[K]) => {
      setParams((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleFile = useCallback(
    async (file: File) => {
      setIsDecoding(true);
      try {
        const buffer = await decodeAudioFile(file);
        engineStop();
        setAudio({ buffer, fileName: file.name });
        // 新しい弾（音源）を装填したら開始位置は先頭に戻す
        setParams((prev) => ({ ...prev, startSec: 0 }));
      } catch (e) {
        console.error("オーディオの読み込みに失敗:", e);
        toast.error("オーディオファイルを読み込めませんでした");
      } finally {
        setIsDecoding(false);
      }
    },
    [engineStop],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void handleFile(file);
      // 同じファイルを選び直せるようにリセットする
      e.target.value = "";
    },
    [handleFile],
  );

  // ---- プリセット（ツールプリセット基盤との接続） ----
  // 音源と開始位置（startSec）は保存しない。「打ち方」の設定のみ。

  const getPresetPayload = useCallback(
    () => buildPresetPayload(params),
    [params],
  );

  const applyPresetPayload = useCallback(
    (payload: unknown): RendaKPresetPayload | null => {
      const sanitized = sanitizePresetPayload(payload);
      if (!sanitized) return null;
      // startSec は現在の値を維持したままプリセット設定を反映する
      setParams((prev) => ({ ...sanitized.params, startSec: prev.startSec }));
      // 適用した正規化済みペイロードを返し、プリセットバーの
      // ダーティ判定スナップショットに使ってもらう。
      return sanitized;
    },
    [],
  );

  /** 未保存プリセットの自動名（例: 140BPM - 1 BAR - 1/16） */
  const defaultPresetName = useMemo(() => {
    const lengthLabel =
      params.lengthSixteenths === 16
        ? "1 BAR"
        : `${params.lengthSixteenths}/16`;
    const startIntervalLabel =
      INTERVAL_STEPS[params.intervalStartStep]?.label ??
      String(params.intervalStartStep);
    return `${params.bpm}BPM - ${lengthLabel} - ${startIntervalLabel}`;
  }, [params.bpm, params.lengthSixteenths, params.intervalStartStep]);

  // WAV / 表示用のファイル名ベース
  const filenameBase = useMemo(() => {
    const src = (audio?.fileName ?? "audio").replace(/\.[^.]+$/, "");
    const safe =
      src
        .replace(/[^\w-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 40) || "audio";
    return `renda-k_${params.bpm}bpm_${params.lengthSixteenths}x16_${safe}`;
  }, [audio, params.bpm, params.lengthSixteenths]);

  const handleDownloadWav = useCallback(async () => {
    if (!audio) return;
    setIsRenderingWav(true);
    try {
      const wav = await renderStutterToWav(audio.buffer, params);
      downloadBlob(wav, `${filenameBase}.wav`, "audio/wav");
    } catch (e) {
      console.error("WAV 書き出しに失敗:", e);
      toast.error("WAV の書き出しに失敗しました");
    } finally {
      setIsRenderingWav(false);
    }
  }, [audio, params, filenameBase]);

  const playheadPhraseSec =
    engine.playheadSec === null ? null : engine.playheadSec - params.startSec;

  return (
    <div className="u-col-center u-gap16" style={{ paddingBlock: 24 }}>
      <div className="u-wrapper">
        <Faceplate>
          <SplatCorner $corner="tl">
            <InkSplat color={SPLAT_CORNERS.tl.color} />
          </SplatCorner>
          <SplatCorner $corner="tr">
            <InkSplat color={SPLAT_CORNERS.tr.color} />
          </SplatCorner>
          <SplatCorner $corner="bl">
            <InkSplat color={SPLAT_CORNERS.bl.color} />
          </SplatCorner>
          <SplatCorner $corner="br">
            <InkSplat color={SPLAT_CORNERS.br.color} />
          </SplatCorner>

          <BrandRow>
            <BrandTitle>
              <SplatGunIcon />
              <BrandName>Renda-K</BrandName>
            </BrandTitle>
            <BrandSub>
              Machine-Gun Stutter — Soremo Labo
              <ToolTip description={HELP_DESCRIPTION} placement="bottom-end">
                <span>
                  <IconButton
                    icon="help_outline"
                    size={18}
                    ariaLabel="ツールの使い方"
                  />
                </span>
              </ToolTip>
            </BrandSub>
          </BrandRow>

          <PresetBar
            toolId={RENDA_K_TOOL_ID}
            schemaVersion={RENDA_K_SCHEMA_VERSION}
            defaultName={defaultPresetName}
            getPayload={getPresetPayload}
            applyPayload={applyPresetPayload}
          />

          <PanelSection title="Ink Tank / Source" stacked>
            <div className="u-col u-gap8">
              <WaveformDisplay
                buffer={audio?.buffer ?? null}
                startSec={params.startSec}
                phraseLenSec={phraseLenSec}
                playheadSec={engine.playheadSec}
                onChangeStartSec={(sec) => setParam("startSec", sec)}
                onDropFile={(file) => void handleFile(file)}
              />
              <div
                className="u-row-between u-gap8"
                style={{ flexWrap: "wrap" }}
              >
                <div className="u-row u-gap8" style={{ minWidth: 0 }}>
                  <InkButton
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isDecoding}
                  >
                    {isDecoding ? "Loading…" : "Load"}
                  </InkButton>
                  <InfoPill>
                    {audio
                      ? `${audio.fileName} — ${formatSec(audio.buffer.duration)}`
                      : "NO AMMO（音源未読み込み）"}
                  </InfoPill>
                </div>
                <InfoPill>START {formatSec(params.startSec)}</InfoPill>
              </div>
              <span
                className="bodytext-11"
                style={{ color: RENDA_PANEL.engraving }}
              >
                波形をクリック / ドラッグで連打開始位置を指定（Shift で微調整）
              </span>
            </div>
          </PanelSection>

          <PanelSection title="Barrel / Interval" stacked>
            <div className="u-col u-gap8">
              <IntervalEditor
                params={params}
                hits={hits}
                playheadPhraseSec={playheadPhraseSec}
                onChangeStartStep={(step) =>
                  setParam("intervalStartStep", step)
                }
                onChangeEndStep={(step) => setParam("intervalEndStep", step)}
              />
              <span
                className="bodytext-11"
                style={{ color: RENDA_PANEL.engraving }}
              >
                左右のハンドル = 連打間隔の開始 / 終了（上ほど速い）。黄色い弾丸
                = 実際の連打タイミング（QUANTIZE・ACCEL 込み）
              </span>
            </div>
          </PanelSection>

          <SectionsGrid>
            <PanelSection title="Trigger">
              <Knob
                label="BPM"
                value={params.bpm}
                min={60}
                max={240}
                defaultValue={DEFAULT_RENDA_PARAMS.bpm}
                onChange={(v) => setParam("bpm", v)}
                color={RENDA_COLORS.soremoBlue}
              />
              <Knob
                label="Length"
                value={params.lengthSixteenths}
                min={1}
                max={16}
                defaultValue={DEFAULT_RENDA_PARAMS.lengthSixteenths}
                detents={16}
                format={(v) => (v === 16 ? "1 BAR" : `${v}/16`)}
                onChange={(v) => setParam("lengthSixteenths", v)}
                color={RENDA_COLORS.soremoGreen}
              />
            </PanelSection>

            <PanelSection title="Fire Control">
              <Knob
                label="Curve"
                value={params.curveAmount}
                min={-100}
                max={100}
                defaultValue={DEFAULT_RENDA_PARAMS.curveAmount}
                bipolar
                format={(v) => (v > 0 ? `+${v}` : `${v}`)}
                onChange={(v) => setParam("curveAmount", v)}
                color={RENDA_COLORS.inkPink}
              />
              <ToggleSwitch
                label="Quantize"
                value={params.quantize}
                onChange={(v) => setParam("quantize", v)}
                color={RENDA_COLORS.inkYellow}
              />
              <Knob
                label="Speed"
                value={params.speed}
                min={0}
                max={100}
                defaultValue={DEFAULT_RENDA_PARAMS.speed}
                format={(v) => `×${speedToPlaybackRate(v).toFixed(2)}`}
                onChange={(v) => setParam("speed", v)}
                color={RENDA_COLORS.inkOrange}
              />
              <Knob
                label="Accel"
                value={params.accel}
                min={0}
                max={100}
                defaultValue={DEFAULT_RENDA_PARAMS.accel}
                format={(v) => `${v}%`}
                onChange={(v) => setParam("accel", v)}
                color={RENDA_COLORS.inkPurple}
              />
            </PanelSection>
          </SectionsGrid>

          <TransportRow>
            <PushButton
              isPlaying={engine.isPlaying}
              disabled={!audio}
              onPress={engine.play}
            />
            <InkButton
              type="button"
              onClick={engine.stop}
              disabled={!engine.isPlaying}
            >
              Stop
            </InkButton>
            <InkButton
              type="button"
              $accent={RENDA_COLORS.inkPink}
              onClick={handleDownloadWav}
              disabled={!audio || isRenderingWav}
            >
              {isRenderingWav ? "Rendering…" : "WAV ↓"}
            </InkButton>
          </TransportRow>

          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            onChange={handleFileInputChange}
            aria-label="オーディオファイルを読み込む"
          />
        </Faceplate>
      </div>
    </div>
  );
};

export default RendaK;
